import type { ShapeEdit } from "~/lib/svgEditor/transform";

/**
 * A preview of attribute edits on live shapes, apart from the document — for
 * a drag, as it goes. What it changed can be put back (`discard`), or
 * forgotten once the document took the edits over and the shapes it was on
 * are gone (`drop`).
 */
export type AttributePreview = {
    apply: (
        shapes: readonly Element[],
        edits: ReadonlyMap<number, ShapeEdit>,
    ) => void;
    discard: () => void;
    drop: () => void;
};

export function createAttributePreview(): AttributePreview {
    /** What the preview changed on the shapes: each attribute as it was. */
    let previewed = new Map<Element, Map<string, string | undefined>>();

    return {
        apply(shapes, edits) {
            for (const [index, edit] of edits) {
                const shape = shapes.at(index);

                if (!shape) continue;

                const saved =
                    previewed.get(shape) ??
                    new Map<string, string | undefined>();

                for (const [name, value] of Object.entries(
                    edit.attributes ?? {},
                )) {
                    if (!saved.has(name)) {
                        saved.set(name, shape.getAttribute(name) ?? undefined);
                    }

                    setAttribute(shape, name, value);
                }

                previewed.set(shape, saved);
            }
        },
        discard() {
            for (const [shape, saved] of previewed) {
                for (const [name, value] of saved) {
                    setAttribute(shape, name, value);
                }
            }

            previewed = new Map();
        },
        drop() {
            previewed = new Map();
        },
    };
}

function setAttribute(shape: Element, name: string, value: string | undefined) {
    if (value === undefined) {
        shape.removeAttribute(name);
    } else {
        shape.setAttribute(name, value);
    }
}
